import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Collapse,
  useTheme,
} from "@mui/material";
import StackedBar from "src/Pages/Assortment/StackedBar.jsx";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CumulativeProductShareChart from "src/Components/Charts/CumulativeProductShareChart/CumulativeProductShareChart";
import { useSelector } from "react-redux";
import { getUrl, getUserDisplayName } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";
import ProgressBar from "src/Components/shared/shared/ProgressBar";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";

const PPATable = ({
  selectedHier: propSelectedHier,
  setSelectedHier: propSetSelectedHier,
  selectedType: propSelectedType,
  setSelectedType: propSetSelectedType,
} = {}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openChartIndex, setOpenChartIndex] = useState(null);
  const projectId = useSelector((state) => state.userProject.projectId);
  const [localSelectedHier, setLocalSelectedHier] = useState(propSelectedHier || "price_bracket");
  const [localSelectedType, setLocalSelectedType] = useState(propSelectedType || "value_sales");
  const selectedHier = propSelectedHier !== undefined ? propSelectedHier : localSelectedHier;
  const selectedType = propSelectedType !== undefined ? propSelectedType : localSelectedType;
  const unitLabel = selectedType === "value_sales" ? " (SAR)" : " (Tons)";

  const resetTableData = () => {
    setData([]);
  };

  const resetTableError = () => {
    setError(null);
  };

  const resetTableLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(
    fetchTableData,
    [resetTableData, resetTableError, resetTableLoading]
  );

  const columns = [
    { label: "", key: "actions" },
    { label: "Brackets", key: "priceBracket" },
    { label: `Total Market Sales${unitLabel}`, key: "tMarket" },
    { label:"wob", key:"wob"},
    { label: `Competition Sales${unitLabel}`, key: "competition" },
    { label: "Competition Market Share", key: "compMS" },
    { label: `${getUserDisplayName()} Sales${unitLabel}`, key: "savola" },
    { label: `${getUserDisplayName()} Market Share`, key: "savolaMS" },
    { label: "Market Share Chart", key: "msChart" },
    { label: "Market Status", key: "marketStatus" },
    { label: "Opportunity Score", key: "opportunityScore" },
    { label: "", key: "reason" },
  ];


  useEffect(() => {
    if (propSelectedHier !== undefined && propSelectedHier !== selectedHier) {
      setLocalSelectedHier(propSelectedHier);
    }
  }, [propSelectedHier]);

  useEffect(() => {
    if (propSelectedType !== undefined && propSelectedType !== selectedType) {
      setLocalSelectedType(propSelectedType);
    }
  }, [propSelectedType]);

  async function fetchTableData() {
    if (!projectId) {
      setError("Project ID not found.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${getUrl()}/market_overview/smart_market_opps?project_id=${projectId}&period=years&target=3&price_or_size=${selectedHier}&value_or_volume=${selectedType}&filters={}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            "x-access-token": localStorage.getItem("userId") || "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const raw = await response.text();

      const cleaned = raw.replace(/\bNaN\b/g, 'null');

      const result = JSON.parse(cleaned);
      console.log(result);
      const safeNumber = (val) => {
        const num = Number(val);
        return Number.isNaN(num) ? "-" : Math.round(num);
      };

      const safeThousandDiv = (val) => {
        const num = Number(val);
        return Number.isNaN(num) ? "-" : Math.round(num / 1000);
      };

      const safeNumberFixed = (val) => {
        const num = Number(val);
        if (Number.isNaN(num)) return "-";
        if (num === 0) return "0";
        return num.toFixed(2);
      };

      const mapped = result.data.map((item) => ({
        name: item[selectedHier] ?? "-",
        tMarket: safeThousandDiv(item["Total Market"]),
        wob: safeNumber(item["WoB"]),
        manufacturerSales: safeThousandDiv(item[`${selectedType}_Manu.`]),
        manufacturerMarketShare: safeNumber(item["Manu. MS"]),
        competition: safeThousandDiv(item[`${selectedType}_Compete.`]),
        compMS: safeNumber(item["Compete MS"]),
        marketStatus: item["Market Status"] ?? "-",
        opportunityScore: safeNumberFixed(item["Opportunity Score"]),
        reason: item["Reason"] ?? "-",
      }));
      const sortedData = mapped?.length
      ? [
          ...mapped.filter((row) => row.name.toLowerCase().includes("total")),
          ...mapped.filter((row) => !row.name.toLowerCase().includes("total")),
        ]
      : [];
      setData(sortedData);
      setError(null);
      console.log(mapped);
      setOpenChartIndex(null);
    } catch (error) {
      console.error("Error fetching market comparison data:", error);
      setError(error.message || "Failed to fetch PPA data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTableData();
  }, [selectedHier, selectedType]);

  const handleChartToggle = (idx) => {
    console.log(idx)
    console.log(openChartIndex)
    setOpenChartIndex((prev) => {
      if (prev === idx) {
        return null;
      }
      return idx;
    });
  };

  const maxTotalMarket = Math.max(...data.map(row => row.tMarket));
  const maxWoB = Math.max(...data.map(row => row.wob));
  const maxCompetition = Math.max(...data.map(row => row.competition));
  const maxCompMS = Math.max(...data.map(row => row.compMS));
  const maxManufacturerSales = Math.max(...data.map(row => row.manufacturerSales));
  const maxManufacturerMS = Math.max(...data.map(row => row.manufacturerMarketShare));

 
  return (
    <Box>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: {
            xs: "0.9rem",
            sm: "1.2rem",
            md: "1.3rem",
          },
          px: {
            xs: 0.5,
            sm: 0.75,
            md: 1,
          },
          color: theme.palette.primary.main,
          textAlign: "center",
          m: 0,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 48,
        }}
      >
        PPA
      </Typography>

      {loading || isRefreshing ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <FacebookCircularProgress
            sx={{ width: { xs: 400, md: 1000 }, maxWidth: "100%" }}
          />
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={`${error} - Click to retry`}
          />
        </Box>
      ) : !data || data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message="No data available - Click to retry"
          />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="center" gap={10} mb={2}>
            <CustomSelectBox
              value={selectedHier}
              onChange={(event) => {
                if (propSetSelectedHier) {
                  propSetSelectedHier(event.target.value);
                } else {
                  setLocalSelectedHier(event.target.value);
                }
              }}
              options={["price_bracket", "size_bracket"]}
            />
            <CustomSelectBox
              value={selectedType}
              onChange={(event) => {
                if (propSetSelectedType) {
                  propSetSelectedType(event.target.value);
                } else {
                  setLocalSelectedType(event.target.value);
                }
              }}
              options={["value_sales", "volume_sales"]}
            />
          </Box>

          <TableContainer
            sx={{ width: "100%", overflowX: "auto", maxHeight: { xs: 700 } }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "100%",
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa", height: 60 }}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "11px", sm: "12px", md: "13px" },
                        color: "#1d1d1d",
                        borderRight:
                          col.key === "actions"
                            ? "none"
                            : col.key === "priceBracket"
                            ? "2px solid #ccc"
                            : col.key === "compMS"
                            ? "2px solid #ccc"
                            : "none",
                        backgroundColor:
                          col.key === "actions"
                            ? "#f3f3f3"
                            : col.key === "priceBracket"
                            ? "#f3f3f3"
                            : [
                                "tMarket",
                                "wob",
                                "competition",
                                "compMS",
                              ].includes(col.key)
                            ? "#eeeeee"
                            : "#e4e4e4",
                        padding: { xs: "4px 6px", sm: "6px 8px" },
                        width:
                          col.key === "actions"
                            ? "0.5%"
                            : col.key === "priceBracket"
                            ? "6%"
                            : col.key === "reason"
                            ? "3%"
                            : col.key === "msChart"
                            ? "10%"
                            : "7%",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data && data.length > 0 ? (
                  data?.map((row, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow
                        sx={{ bgcolor: row.highlight ? "#ffe6e6" : "#fff" }}
                        id={idx}
                      >
                        <TableCell align="center">
                          <Tooltip
                            title={
                              openChartIndex === idx
                                ? "Hide Chart"
                                : "Show Breakdown Chart"
                            }
                          >
                            <IconButton
                              onClick={() => {
                                handleChartToggle(idx);
                                console.log(idx);
                              }}
                            >
                              {openChartIndex === idx ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: "2px solid #ccc",
                            fontSize: { xs: "11px", sm: "12px" },
                            padding: { xs: "4px 6px", sm: "6px 8px" },
                          }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell align="center">
                          <ProgressBar
                            value={row.tMarket}
                            maxValue={maxTotalMarket}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ProgressBar
                            value={row.wob}
                            maxValue={maxWoB}
                            showPercentage
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ProgressBar
                            value={row.competition}
                            maxValue={maxCompetition}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: "2px solid #ccc",
                          }}
                        >
                          <ProgressBar
                            value={row.compMS}
                            maxValue={maxCompMS}
                            showPercentage
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ProgressBar
                            value={row.manufacturerSales}
                            maxValue={maxManufacturerSales}
                            type="manufacturer"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ProgressBar
                            value={row.manufacturerMarketShare}
                            maxValue={maxManufacturerMS}
                            showPercentage
                            type="manufacturer"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <StackedBar
                            brands={[
                              {
                                name: "Manufacturer",
                                value: row.manufacturerMarketShare,
                                color: "#21A0FF",
                              },
                              {
                                name: "Competition",
                                value: row.compMS,
                                color: "#08306B",
                              },
                            ]}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: { xs: "11px", sm: "12px" },
                            padding: { xs: "4px 6px", sm: "6px 8px" },
                            color:
                              row.marketStatus?.toLowerCase() === "opportunity"
                                ? theme.palette?.indicator?.safe || "#2e7d32"
                                : row.marketStatus?.toLowerCase() === "threat"
                                ? theme.palette?.indicator?.threat || "#d10000"
                                : row.marketStatus?.toLowerCase() === "neutral"
                                ? theme.palette?.indicator?.warning || "#f58327"
                                : "#000000",
                            fontWeight: 600,
                          }}
                        >
                          {row.marketStatus}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: { xs: "11px", sm: "12px" },
                            padding: { xs: "4px 6px", sm: "6px 8px" },
                          }}
                        >
                          {row.opportunityScore}
                        </TableCell>
                        <TableCell align="center">
                          {row.reason && row.reason !== "-" ? (
                            <Tooltip title={row.reason} arrow placement="top">
                              <IconButton
                                size="small"
                                sx={{
                                  padding: "4px",
                                  color: "#666",
                                  "&:hover": {
                                    color: "#1976d2",
                                  },
                                }}
                              >
                                <HelpOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Only render collapse row when this specific row is open */}
                      {openChartIndex === idx && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            sx={{ p: 0, border: "none" }}
                          >
                            <Collapse in={openChartIndex === idx} timeout={300}>
                              <Box
                                sx={{
                                  p: { xs: 1, sm: 2 },
                                  width: "100%",
                                  overflowX: "auto",
                                }}
                              >
                                <CumulativeProductShareChart
                                  key={`chart-${idx}-${row.name}`}
                                  apiData={row.name}
                                  minimal={true}
                                  sx={{
                                    width: "100%",
                                    maxWidth: { xs: "100%", md: "900px" },
                                  }}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <React.Fragment key={0}>
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        No data available.
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-end"
            mt={1}
            sx={{ ml: 5, mr: 10 }}
          ></Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            gap={3}
            sx={{ mr: 10 }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: "#21A0FF",
                  borderRadius: "3px",
                }}
              />
              <Typography variant="body2">Savola</Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: "#08306B",
                  borderRadius: "3px",
                }}
              />
              <Typography variant="body2">Competition</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PPATable;
